<script setup lang="ts">
import { Splide, SplideSlide } from '@splidejs/vue-splide'
import '@splidejs/vue-splide/css'
import TestimonialItem from '~/components/Product/LandingPages/Testimonialtem.vue';

type Testimonial = {
	image?: string
	text: string
	authorImage?: string
	authorName?: string
}

const props = withDefaults(defineProps<{
		header?: string
		subHeader?: string
		paragraph?: string
		items?: Testimonial[]
		slider?: boolean
		perPage?: number
	}>(),
	{
		header: '',
		subHeader: '',
		paragraph: '',
		items: [],
		subText: '',
		slider: false,
		perPage: 3,
	},
)

const sliderElement = ref(null)

const options = {
	perPage: props.perPage,
	perMove: 1,
	gap: '2rem',
	arrows: false,
	pagination: false,
	breakpoints: {
		1024: {
			perPage: 1,
			gap: '2rem',
		},
	},
}

const mediaQuery = ref(null)
const device = ref('desktop')

onMounted(() => {
	mediaQuery.value = window.matchMedia('(max-width: 1024px)')
	device.value = mediaQuery.value.matches ? 'mobile' : 'desktop'

	mediaQuery.value.addEventListener('change', (e) => {
		device.value = e.matches ? 'mobile' : 'desktop'
	})
})

onUnmounted(() => {
	if (mediaQuery.value) {
		mediaQuery.value.removeEventListener('change', (e) => {
			device.value = e.matches ? 'mobile' : 'desktop'
		})
	}
})

const wrapperClasses = computed(() => {
	return props.slider ? 'w-full' : 'grid gap-8 md:grid-cols-3'
})

const currentIndex = ref(0)

const handleMove = splide => {
	currentIndex.value = splide.index
}

const showPrevButton = computed(() => {
	return currentIndex.value > 0
})

const showNextButton = computed(() => {
	return currentIndex.value < (props.items || []).length - (device.value === 'mobile' ? 1 : props.perPage);
})
</script>

<template>
	<div class="bg-gray-100 relative">
		<div class="flex flex-col max-w-7xl mx-auto px-4 sm:px-6 lg:px-16 py-12 relative text-center">
			<div class="mb-8">
				<slot name="header">
					<h2 v-if="props.header" class="text-4xl font-semibold text-gray-900 text-start mt-0">{{ props.header }}</h2>
				</slot>

				<slot name="subHeader">
					<p v-if="props.subHeader" class="text-lg text-gray-600 text-start">{{ props.subHeader }}</p>
				</slot>

				<slot name="paragraph">
					<p v-if="props.paragraph" class="text-start mb-0" v-html="props.paragraph"></p>
				</slot>
			</div>

			<component
				:is="slider ? Splide : 'div'"
				ref="sliderElement"
				:class="wrapperClasses"
				:options="options"
				@splide:moved="handleMove">
				<component
					:is="slider ? SplideSlide : 'div'"
					v-for="(item, index) in props.items"
					:key="index"
					@move="handleMove">
					<TestimonialItem
						:text="item.text"
						:image="item.image"
						:author-image="item.authorImage"
						:author-name="item.authorName"
					/>
				</component>
			</component>

			<div
				v-if="slider"
				class="flex align-items-center mt-8"
			>
				<button
					:disabled="!showPrevButton"
					class="disabled:opacity-30 lg:disabled:hidden lg:absolute lg:left-4 lg:top-1/2 lg:transform lg:-translate-y-1/2 hover:-translate-x-0.5 transition-transform"
					@click="() => sliderElement.go('-1')">
					<Icon name="ph:caret-left-bold" class="text-2xl" />
				</button>

				<div class="flex justify-center items-center w-full gap-2">
					<template
						v-for="(item, index) in (device === 'mobile' ? props.items : Math.floor((props.items || []).length / props.perPage))"
						:key="index"
					>
						<button
							:disabled="(device === 'mobile' ? currentIndex : Math.floor(currentIndex / props.perPage)) === index"
							class="w-4 h-4 rounded-full bg-gray-300 disabled:bg-black"
							@click="() => sliderElement.go(device === 'mobile' ? index : index * props.perPage)"></button>
					</template>
				</div>

				<button
					:disabled="!showNextButton"
					class="disabled:opacity-30 lg:disabled:hidden lg:absolute lg:right-4 lg:top-1/2 lg:transform lg:-translate-y-1/2 hover:translate-x-0.5 transition-transform"
					@click="() => sliderElement.go('+1')">
					<Icon name="ph:caret-right-bold" class="text-2xl" />
				</button>
			</div>

			<slot name="footer"></slot>
		</div>
	</div>
</template>
